import { LoaderCircle } from 'lucide-react';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../lib/cn';

export interface SpinnerProps extends HTMLAttributes<HTMLSpanElement> {
  /** Pixel size of the icon (default 14, as in button loading states). */
  size?: number;
  /** Stroke width (default 3). */
  strokeWidth?: number;
  /**
   * Accessible label. When set the spinner is announced (`role="status"`); otherwise it is
   * decorative (e.g. inside a button whose text already says "Saving…").
   */
  label?: string;
}

/** Circular loading indicator (Lucide `loader-circle`, 1 turn/s). Keeps spinning under reduced motion. */
export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(function Spinner(
  { size = 14, strokeWidth = 3, label, className, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      role={label ? 'status' : undefined}
      aria-hidden={label ? undefined : true}
      className={cn('inline-flex shrink-0', className)}
      {...rest}
    >
      <LoaderCircle
        data-motion="essential"
        className="animate-spin"
        width={size}
        height={size}
        strokeWidth={strokeWidth}
        aria-hidden
      />
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
});
